import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, InfoIcon, XIcon } from 'lucide-react';
import { isRtl, type Lang } from './translations';
export function IntakeQuestionSlide({
  question,
  options,
  infoText,
  backgroundImage,
  onSelect,
  onBack,
  lang = 'en' as Lang
}: {
  question: string;
  options: string[];
  infoText?: string;
  backgroundImage?: string;
  onSelect: (option: string) => void;
  onBack: () => void;
  lang?: Lang;
}) {
  const [showInfo, setShowInfo] = useState(false);
  const rtl = isRtl(lang);
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-white" dir={rtl ? 'rtl' : 'ltr'} data-id="element-403">
      {/* Blurry centered background image — atmospheric, no frame */}
      {backgroundImage && <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none flex items-center justify-center" data-id="element-404">
          <motion.img initial={{
        opacity: 0
      }} animate={{
        opacity: 0.25
      }} transition={{
        duration: 1.2
      }} src={backgroundImage} className="w-[120%] h-[70%] object-cover object-center blur-sm" style={{
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)'
      }} alt="" data-id="element-405" />
        </div>}

      {/* Top bar */}
      <div className="flex items-center justify-between mb-8 relative z-10" data-id="element-406">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-stone-600 transition-colors bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-stone-100" aria-label="Go back" data-id="element-407">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-408" />
        </button>
      </div>

      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full relative z-10" data-id="element-409">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8 relative" data-id="element-410">
          <h2 className="text-2xl font-semibold text-slate-900 leading-tight pr-8" data-id="element-411">
            {question}
          </h2>

          {infoText && <button onClick={() => setShowInfo(!showInfo)} className="absolute top-1 right-0 text-nhs-blue hover:text-nhs-darkblue p-1 bg-nhs-blue/10 rounded-full" aria-label="More information" data-id="element-412">
              <InfoIcon className="w-5 h-5" data-id="element-413" />
            </button>}

          <AnimatePresence data-id="element-414">
            {showInfo && infoText && <motion.div initial={{
            opacity: 0,
            height: 0,
            marginTop: 0
          }} animate={{
            opacity: 1,
            height: 'auto',
            marginTop: 16
          }} exit={{
            opacity: 0,
            height: 0,
            marginTop: 0
          }} className="overflow-hidden" data-id="element-415">
                <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl text-sm text-amber-800 flex items-start" data-id="element-416">
                  <p className="flex-1" data-id="element-417">{infoText}</p>
                  <button onClick={() => setShowInfo(false)} className="ml-3 text-amber-600 hover:text-amber-800" data-id="element-418">
                    <XIcon className="w-4 h-4" data-id="element-419" />
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        <div className="space-y-3 mt-auto mb-12" data-id="element-420">
          {options.map((option, index) => <motion.button key={option} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + index * 0.1
        }} onClick={() => {
          setTimeout(() => onSelect(option), 150);
        }} className="w-full text-left p-5 rounded-xl border border-stone-200 bg-white shadow-sm hover:border-nhs-blue hover:ring-1 hover:ring-nhs-blue transition-all duration-200 group active:scale-[0.98]" data-id="element-421">
              <span className="text-stone-700 font-medium text-lg group-hover:text-nhs-blue transition-colors" data-id="element-422">
                {option}
              </span>
            </motion.button>)}
        </div>
      </div>
    </div>;
}